import {Button} from "finform";
import React, { ReactElement, ReactNode, useLayoutEffect, useState } from 'react';
import Ext from "react-ext-state";

import { getTaskClients, type TaskContactDTO } from "#app/api";
import ModalForm from "#app/components/forms/ModalForm/ModalForm";
import {Row} from "#app/components/layout/styles";
import DeleteButton from "#app/components/relations/components/DeleteButton";
import EventTaskForm from "#app/components/relations/Tasks/form/TaskFormTypes/EventTaskForm";
import NewTaskForm from "#app/components/relations/Tasks/form/TaskFormTypes/NewTaskForm";
import useTaskActions from "#app/components/relations/Tasks/hooks/useTaskActions";
import {useTaskForm} from "#app/components/relations/Tasks/hooks/useTaskForm";
import {TASK_FORM_TYPES} from "#app/store/reducers/task";

export { CLIENT_TYPE, CONTACT_TYPE } from '#app/components/relations/constants/taskEntityTypes';

interface TaskFormProps {
    children?: ReactNode;
    modalVisible: boolean;
    setModalVisible: (visible: boolean) => void;
    form: Record<string, unknown>;
    taskType: { id: string; title: string };
    setTaskType: (type: { id: string; title: string }) => void;
    setForm: (form: Record<string, unknown>) => void;
    callback: () => void;
    auth: { data: { id: number } };
}

const TaskForm: React.FC<TaskFormProps> = ({
                      children,
                      modalVisible,
                      setModalVisible,
                      form,
                      taskType,
                      setTaskType,
                      setForm,
                      callback,
                      auth,
                  }): ReactElement | null => {
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [contacts, setContacts] = useState<TaskContactDTO[]>([]);

    useLayoutEffect(() => {
        if (modalVisible) getTaskClients(auth.data.id, setContacts);
    }, [auth.data.id, modalVisible]);

    const callbackOnTaskUpdate = () => {
        callback();
        Ext.setState('crm-contact-task', 1)
    };

    const {
        id, setId,
        type, setType,
        typeId, setTypeId,
        comment, setComment,
        contactId, setContactId,
        startDate, setStartDate,
        endDate, setEndDate,
        repeatId, setRepeatId,
        closeForm,
        getError,
        changeFormErrors,
    } = useTaskForm(form, setModalVisible, setForm);

    const {handleDelete, handleUpdate, handleCreate} = useTaskActions(
        id, comment, startDate, endDate, typeId, contacts, contactId, repeatId,
        closeForm, callbackOnTaskUpdate, changeFormErrors, setId, loading, setLoading
    );

    const renderButtons = () => {
        return (
            <Row>
                {id && (<DeleteButton handleDelete={handleDelete}/>)}
                <Row style={{display: 'block', textAlign: 'center'}}>
                    <Button type="cancel" withMargin narrow onClick={closeForm}>
                        Отмена
                    </Button>
                    <Button
                        withMargin
                        narrow
                        loading={loading}
                        id="task-form__save-button"
                        onClick={(id) ? handleUpdate : handleCreate}
                    >
                        Сохранить
                    </Button>
                </Row>
            </Row>
        )
    }

    const getTaskName = () => {
        if (taskType.id === 'event') {
            return taskType.title;
        } else return id ? 'Редактирование' : 'Новое событие';
    };

    if (!modalVisible) {
        return null;
    }

    // @TODO слишком много пропсов у NewTaskForm
    return (
        <ModalForm
            header={(() => getTaskName())()}
            modalVisible={modalVisible}
            closeForm={closeForm}
            width='760px'
            closeId="modal__close-task-form"
            noScroll
            allowRelativeWidthOnMedia={false}
        >
            {(taskType.id === TASK_FORM_TYPES.EVENT.id) ? (
                <EventTaskForm
                    form={form}
                    id={id} typeId={typeId}
                    startDate={startDate} endDate={endDate}
                    setTaskType={setTaskType}
                    onUpdate={callbackOnTaskUpdate}
                />
            ) : (
                <NewTaskForm
                    type={type} setType={setType}
                    typeId={typeId} setTypeId={setTypeId}
                    comment={comment} setComment={setComment}
                    startDate={startDate} setStartDate={setStartDate}
                    endDate={endDate} setEndDate={setEndDate}
                    contactId={contactId} setContactId={setContactId}
                    search={search} setSearch={setSearch}
                    repeatId={repeatId} setRepeatId={setRepeatId}
                    renderButtons={renderButtons}
                    children={children}
                    contacts={contacts}
                    id={id}
                    getError={getError}
                />
            )}
        </ModalForm>
    )
}

export default TaskForm